import { action, observable } from 'mobx';

import { authFetch } from 'core/client';
import { AppManager } from 'core/index';
import { clientStore } from 'apps/client/app';

import { MeshAnalysisSubscription } from 'libs/rendering/meshAnalysis/gql';
import { isHTML } from 'libs/rendering/utils';

import uuid from 'uuid/v4';

import { DfmProcessCutoffs } from 'apps/rfq/dfmConfig';
import { routerStore } from 'core/routing';

// DFM Issues for a DFM feature ("wall thicknes", "hole radius"... etc)
export class DfmFeatureIssues {
  title = null; // DFM feature UI name
  type = null; // DFM feature type (wasm enum)
  wasmObj = null; // DFM Issues object (obserbable-wrapped wasm class pointer)

  constructor(dfm, type, title) {
    if (dfm && type) {
      this.title = title;
      this.type = type;
      this.wasmObj = dfm.GetResult(type);
    }
  }

  get warningCount() {
    return this.wasmObj ? this.wasmObj.warningCount : 0;
  }

  // wraps this.wasmObj in a JS array
  get warningList() {
    if (!this.wasmObj) return null;

    const warnings = [];
    for (let i = 0; i < this.wasmObj.warningCount; i++) {
      const warning = this.wasmObj.GetWarning(i);
      warnings.push(warning);
    }
    return warnings;
  }
}

// wrapper for DFM cutoff analysis results
export class DfmCutoffAnalysis {
  @observable processKey = null;
  @observable body = null;

  constructor(key, dfm, dfmEnum) {
    if (dfm && dfmEnum) {
      this.processKey = key;
      this.body = dfm.GetBody();

      if (key) {
        this.sharpness = new DfmFeatureIssues(
          dfm,
          dfmEnum.DfmAnalysisSharpEdges,
          'Sharp Edges',
        );
        this.thickness = new DfmFeatureIssues(
          dfm,
          dfmEnum.DfmAnalysisThickness,
          'Wall Thickness',
        );
        this.holeRadius = new DfmFeatureIssues(
          dfm,
          dfmEnum.DfmAnalysisHoleRadius,
          'Hole Radius',
        );
        this.embossed = new DfmFeatureIssues(
          dfm,
          dfmEnum.DfmAnalysisEmbossed,
          'Embossed Height',
        );
        this.engraved = new DfmFeatureIssues(
          dfm,
          dfmEnum.DfmAnalysisEngraved,
          'Engraved Depth',
        );
      }
    }
  }

  get isEmpty() {
    return this.processKey === null || this.body === null;
  }

  get hasIssues() {
    return (
      this.sharpness?.warningCount > 0 ||
      this.thickness?.warningCount > 0 ||
      this.holeRadius?.warningCount > 0 ||
      this.embossed?.warningCount > 0 ||
      this.engraved?.warningCount > 0
    );
  }

  get featureIssues() {
    return [
      this.sharpness,
      this.thickness,
      this.holeRadius,
      this.embossed,
      this.engraved,
    ];
  }
}

export const DfmStatus = {
  SUBSCRIBED: 'SUBSCRIBED',
  REQUESTED: 'REQUESTED',
  STARTED: 'STARTED',
  FINISHED: 'FINISHED',
  FETCHING: 'FETCHING',
  DONE: 'DONE',
  CUTOFF: 'CUTOFF',
};

export class MeshAnalysisManager extends AppManager {
  @observable dfmBody = null;
  @observable dfmStatus = null;
  @observable dfmError = null;
  @observable lastCutoffAnalysis = new DfmCutoffAnalysis();

  requestId = uuid();
  requestUrl = null;
  startTime = null;
  ellapsedTime = null;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
  }

  get hasStarted() {
    return this.dfmStatus !== DfmStatus.SUBSCRIBED;
  }

  get isWaiting() {
    return (
      !this.dfmError &&
      (this.dfmStatus === DfmStatus.REQUESTED ||
        this.dfmStatus === DfmStatus.STARTED ||
        this.dfmStatus === DfmStatus.FINISHED ||
        this.dfmStatus === DfmStatus.FETCHING)
    );
  }

  get hasFinished() {
    return (
      this.dfmError ||
      this.dfmStatus === DfmStatus.DONE ||
      this.dfmStatus === DfmStatus.CUTOFF
    );
  }

  get statusMsg() {
    if (this.isWaiting) {
      return this.dfmStatus === DfmStatus.REQUESTED
        ? 'Requesting DFM analysis...'
        : this.dfmStatus === DfmStatus.STARTED
        ? 'Running DFM analysis...'
        : 'Fetching DFM results...';
    }
    return null;
  }

  get visible() {
    return routerStore?.route.params.panel === 'dfm';
  }

  get isSubscribed() {
    return this.meshAnalysisSubscription?.subscription !== null;
  }

  // called on viewerControl.Unmount
  @action UnsubscribeMeshAnalysis = () => {
    if (this.meshAnalysisSubscription) {
      this.meshAnalysisSubscription.CompleteSubscription();
      this.dfmStatus = null;
      this.dfmError = null;
      this.lastCutoffAnalysis = new DfmCutoffAnalysis();
    }
  };

  // called on every analysis request, as we could be requesting
  //  mesh analyses on different CadFiles.
  @action subscribeMeshAnalysis = (fileId, contextId) => {
    if (!clientStore.hasRole('INTERMEDIATOR')) {
      this.requestUrl = null;
      return;
    }

    if (!fileId || !contextId) {
      console.warn('no valid file or context IDs for mesh analysis');
      this.requestUrl = null;
      return;
    }

    // console.log('subscribe mesh anaylsis', fileId, contextId);
    this.requestUrl = `${MAIN_URL}/wasm/${this.requestId}/${fileId}/meshAnalysis`;

    this.meshAnalysisSubscription = new MeshAnalysisSubscription(this, {
      variables: {
        subscribeMeshAnalysisInput: {
          contextId,
          fileId,
          requestId: this.requestId,
        },
      },
    });
    this.meshAnalysisSubscription.process(this.handleMeshAnalysisMessage);
    this.meshAnalysisSubscription.StartSubscription();

    this.lastCutoffAnalysis = new DfmCutoffAnalysis();
    this.dfmStatus = DfmStatus.SUBSCRIBED;
  };

  @action handleMeshAnalysisMessage = message => {
    // console.log(message);
    const { url, status, error, errorList } = message;
    this.dfmStatus = status;
    this.dfmError = error;

    if (status === DfmStatus.STARTED) {
      this.startTime = performance.now();
    } else {
      const endTime = performance.now();
      this.ellapsedTime = endTime - this.startTime;
    }

    if (url && status === DfmStatus.FINISHED) {
      // below [authFetch] may take a while if the file is big...
      this.dfmStatus = DfmStatus.FETCHING;
      authFetch(url)
        .then(response => response.text())
        .then(txt => {
          // load DFM analysis data into body
          this.dfmBody.LoadDfmAnalysisData(txt);
          this.dfmStatus = DfmStatus.DONE;
        });
    } else if (errorList && status === 'ERRORLIST') {
      // NOTE: by now, cutoff analysis runs locally (viewerControl.RequestCutoffAnalysis).
      //       If in the future it also runs on the server, here is where it needs to be get.
      console.warn(
        'not implemented: receiving cutoff analysis results from the server',
      );
      // this.lastCutoffAnalysis = new DfmCutoffAnalysis(errorList);
      // this.dfmStatus = DfmStatus.CUTOFF;
    }
  };

  @action RequestMeshAnalysis = (
    dfmBody,
    fileId,
    contextId,
    cutoffs = null,
    buildDir = null,
    topologyOnly = false,
  ) => {
    // prepare subscrition
    this.subscribeMeshAnalysis(fileId, contextId);
    if (!this.requestUrl) {
      return;
    }

    // check body
    if (!dfmBody || !dfmBody.uuid) {
      console.warn('no valid body ID for mesh analysis');
      return;
    }
    this.dfmBody = dfmBody;

    // construct the MeshAnalysisParameters
    const parameters = { bodyId: dfmBody.uuid };
    if (topologyOnly) {
      parameters.topologyOnly = true;
    } else if (cutoffs && cutoffs instanceof DfmProcessCutoffs) {
      parameters.thicknessCutOff = cutoffs.thickness;
      parameters.sharpEdgesCutOff = cutoffs.sharpnes;
      parameters.holeRadiusCutOff = cutoffs.holeRadius;
      parameters.ebossementCutOff = cutoffs.embossement;
      parameters.engravementCutOff = cutoffs.engravement;

      // NOTE: buldDir is not used, maybe in the future
      if (buildDir && buildDir.length === 3) {
        parameters.buildDir = buildDir;
      }
    }

    // send mesh analysis request to server
    this.dfmStatus = DfmStatus.REQUESTED;
    this.lastCutoffAnalysis = new DfmCutoffAnalysis();
    authFetch(this.requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(parameters),
    })
      .then(response => response.text())
      .then(txt => {
        if (!isHTML(txt)) {
          const bodyJson = JSON.parse(txt);
          const { status, error, errorList } = bodyJson;
          this.dfmStatus = status;
          this.dfmError = error;
          this.lastCutoffAnalysis = new DfmCutoffAnalysis(errorList);
        } else {
          console.warn('bad mesh analysis response', txt);
          this.dfmStatus = DfmStatus.FINISHED;
          this.dfmError = 'bad mesh analysis response';
        }
      });
  };
}
